import { Badge, Box, Flex, Stack, Text } from '@chakra-ui/react';
import type { DecisionDay } from '@fluenceur/types';
import { useTranslation } from 'react-i18next';
import {
  TYPE_PALETTE,
  formatTooltipDay,
  gestureLabel,
  typeLabel,
  whoLabel,
} from './decision-format';

/** How many decisions the tooltip names before « + N autres décisions ». */
export const TOOLTIP_VISIBLE_DECISIONS = 3;

export interface DecisionTooltipProps {
  day: DecisionDay;
  /** The strip's first day: its header says « aujourd'hui ». */
  isToday: boolean;
  /** The dashboard names each decision's type; the UGC page holds one type only. */
  showType: boolean;
}

/**
 * What a day of the strip holds, as a small card: the day in words, how many
 * decisions, the first three (gesture, person, and the type on the dashboard),
 * then how many more. Bordered, never shadowed.
 */
export const DecisionTooltip = ({ day, isToday, showType }: DecisionTooltipProps) => {
  const { t, i18n } = useTranslation();
  const locale = i18n.language;
  const shown = day.decisions.slice(0, TOOLTIP_VISIBLE_DECISIONS);
  const more = day.decisions.length - shown.length;
  const header = [
    formatTooltipDay(day.date, locale),
    isToday ? t(`decisionStrip.todaySuffix`) : null,
  ]
    .filter(Boolean)
    .join(' · ');

  return (
    <Box
      data-testid="decision-tooltip"
      w="300px"
      maxW="calc(100vw - 32px)"
      bg="panelBg"
      color="ink"
      borderWidth="1px"
      borderColor="border.emphasized"
      rounded="12px"
      overflow="hidden"
      textAlign="left"
    >
      <Box px="14px" py="10px" bg="background" borderBottomWidth="1px" borderColor="border.subtle">
        <Text fontFamily="heading" fontSize="13.5px" fontWeight={600}>
          {header}
        </Text>
      </Box>
      <Stack gap="8px" px="14px" pt="10px" pb="12px">
        <Text
          fontSize="12.5px"
          fontWeight={500}
          color="inkMuted"
          data-testid="decision-tooltip-count"
        >
          {t(`decisionStrip.count`, { count: day.decisions.length })}
        </Text>
        {shown.map((decision) => (
          <Flex key={decision.id} align="center" gap="8px" data-testid="decision-tooltip-item">
            <Box boxSize="6px" rounded="full" bg="yellow.600" flexShrink={0} aria-hidden="true" />
            <Stack gap="0" flex="1" minW="0">
              <Text fontSize="13px" fontWeight={500} lineClamp={1}>
                {gestureLabel(t, decision, locale)}
              </Text>
              <Text fontSize="12px" color="inkMuted" lineClamp={1}>
                {whoLabel(t, decision)}
              </Text>
            </Stack>
            {showType ? (
              <Badge
                colorPalette={TYPE_PALETTE[decision.type]}
                variant="subtle"
                size="sm"
                flexShrink={0}
                data-testid="decision-type"
              >
                {typeLabel(t, decision.type)}
              </Badge>
            ) : null}
          </Flex>
        ))}
        {more > 0 ? (
          <Flex align="center" gap="8px" data-testid="decision-tooltip-more">
            <Box boxSize="6px" rounded="full" bg="border.emphasized" aria-hidden="true" />
            <Text fontSize="12.5px" color="inkMuted">
              {t(`decisionStrip.more`, { count: more })}
            </Text>
          </Flex>
        ) : null}
      </Stack>
    </Box>
  );
};
